import { useEffect, useRef, useState } from "react";
const heroVideo = "/__l5e/assets-v1/f5d9ec42-fd40-463d-8d47-892266db4415/hero-bg.mp4";

/**
 * Hero background: renders a responsive <picture> poster (mobile/desktop WebP)
 * as the LCP element, then lazily upgrades to a background video on capable
 * desktops only. Poster URLs are stable /public assets so they can be
 * preloaded from index.html and served without waiting on JS.
 */
export function LazyHeroVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [show, setShow] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth < 1024) return; // desktop-only
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // @ts-expect-error: non-standard connection API
    const conn = navigator.connection;
    const slow =
      conn?.saveData ||
      ["slow-2g", "2g", "3g"].includes(conn?.effectiveType ?? "");
    // @ts-expect-error: non-standard
    const lowMem = (navigator.deviceMemory && navigator.deviceMemory < 4);
    const lowCPU = (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4);
    if (reduce || slow || lowMem || lowCPU) return;

    const start = () => setShow(true);
    const idle = (window as any).requestIdleCallback
      ? (window as any).requestIdleCallback(start, { timeout: 3500 })
      : window.setTimeout(start, 2500);
    return () => {
      if ((window as any).cancelIdleCallback) (window as any).cancelIdleCallback(idle);
      else window.clearTimeout(idle as number);
    };
  }, []);

  return (
    <>
      <picture>
        <source media="(max-width: 767px)" srcSet="/hero-bg-mobile.webp" type="image/webp" />
        <source srcSet="/hero-bg.webp" type="image/webp" />
        <img
          src="/hero-bg.webp"
          alt="Athlete training at Valorform performance studio in Munich — science-based coaching, VO2 max and lactate testing"
          fetchPriority="high"
          decoding="async"
          width={1600}
          height={900}
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
      </picture>
      {show && !failed && (
        <video
          ref={ref}
          src={heroVideo}
          poster="/hero-bg.webp"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          onLoadedData={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-700 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </>
  );
}

export default LazyHeroVideo;
